import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDaysIcon, MapPinIcon, TruckIcon, UserGroupIcon, ClockIcon } from '@heroicons/react/24/outline';

/**
 * Order summary / price-breakdown card shown on PaymentSuccess.
 *
 * Mirrors the "order summary" aside card in screens/Booking/thankyou.js
 * (same rounded-2xl / shadow-card / Row helper visual language) so the
 * payment confirmation feels like a continuation of the booking flow.
 *
 * Used only by screens/Payment/PaymentSuccess.js (confirmed via grep), so
 * internals were restructured freely; the props accepted are unchanged so
 * the call site didn't need to change.
 */
const TransactionDetail = ({
  orderInfo = null,
  transactionDetail = null,
  title,
  price,
  departCity,
  carpool,
  qte,
  total,
  totalPay,
  theRest,
  prices = true,
  coupon = {},
  userDiscount = {},
  zeroPaid = true,
}) => {
  const { t } = useTranslation();
  const event = orderInfo?.event_infos ?? orderInfo?.event ?? {};
  const [transaction, setTransactionDetail] = React.useState(null);

  function formatDateWithTime(timestamp) {
    const dateObj = new Date(timestamp);
    const date = dateObj.toDateString();
    const hour = String(dateObj.getHours()).padStart(2, '0');
    const minute = String(dateObj.getMinutes()).padStart(2, '0');
    return `${date} ${hour}:${minute}`;
  }

  React.useEffect(() => {
    if (transactionDetail && transactionDetail?.payment_details?.transactions?.length) {
      setTransactionDetail(
        transactionDetail.payment_details.transactions[
          transactionDetail.payment_details.transactions.length - 1
        ]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasWelcomeDiscount = Object.keys(userDiscount || {}).length && parseInt(userDiscount.value, 10);
  const hasCoupon =
    Object.keys(coupon || {}).length && parseInt(coupon.value, 10) && !parseInt(userDiscount?.value, 10);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card">
      <div className="border-b border-slate-100 px-5 py-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-display text-base font-semibold text-ink">{title || t('payment.summaryTitle')}</h3>
          {price ? <bdi className="text-sm font-semibold text-ink-muted">{price}</bdi> : null}
        </div>
      </div>

      <div className="space-y-2.5 px-5 py-4 text-sm">
        {departCity && departCity !== '1' ? (
          <Row icon={MapPinIcon} label={t('payment.departureCity')} value={departCity} />
        ) : null}
        {event.type !== 'Activitie' && carpool ? (
          <Row icon={TruckIcon} label={t('payment.carpool')} value={carpool} />
        ) : null}
        <Row icon={UserGroupIcon} label={t('payment.guests')} value={qte} />

        {event.type === 'Activitie' ? (
          <>
            {orderInfo?.date_activity ? (
              <Row icon={CalendarDaysIcon} label={t('payment.bookingDate')} value={orderInfo.date_activity} />
            ) : null}
            {parseInt(orderInfo?.hour_status || event.hour_status, 10) ? (
              <Row icon={ClockIcon} label={t('payment.timeSlot')} value={orderInfo?.time_activity} />
            ) : null}
          </>
        ) : null}

        {event.type === 'Event' ? (
          <>
            {event.date_depart ? (
              <Row
                icon={CalendarDaysIcon}
                label={t('payment.departureDate')}
                value={new Date(event.date_depart).toLocaleDateString('fr-FR')}
              />
            ) : null}
            {event.hour_depart ? <Row icon={ClockIcon} label={t('payment.departureTime')} value={event.hour_depart} /> : null}
          </>
        ) : null}
      </div>

      <div className="space-y-2 border-t border-slate-100 px-5 py-4 text-sm">
        {prices ? (
          <div className="flex items-center justify-between text-ink-muted">
            <span>{t('payment.subtotal')}</span>
            <bdi>{prices}</bdi>
          </div>
        ) : null}

        {hasWelcomeDiscount ? (
          <div className="flex items-center justify-between text-emerald-600">
            <span>
              {t('payment.welcomeDiscount')} -{userDiscount.value}%
            </span>
            <bdi>{userDiscount.price}</bdi>
          </div>
        ) : null}

        {hasCoupon ? (
          <div className="flex items-center justify-between text-emerald-600">
            <span>
              {t('payment.promoDiscount')} -{coupon.value}%
            </span>
            <bdi>{coupon.price}</bdi>
          </div>
        ) : null}

        <div className="flex items-center justify-between pt-1">
          <span className="font-display text-base font-semibold text-ink">{t('payment.total')}</span>
          <bdi className="font-display text-xl font-bold text-brand-500">{total}</bdi>
        </div>

        <div className="mt-2 space-y-1 rounded-xl bg-surface-muted p-3 text-xs">
          <div className="flex items-center justify-between text-ink-subtle">
            <span>{t('payment.restOnSite')}</span>
            <bdi>{theRest}</bdi>
          </div>
          {zeroPaid ? (
            <div className="flex items-center justify-between text-ink">
              <span>{t('payment.totalPaid')}</span>
              <bdi className="font-semibold">
                {totalPay} {transaction?.currency ?? 'MAD'}
              </bdi>
            </div>
          ) : null}
        </div>

        {transaction ? (
          <div className="flex items-center justify-between pt-1 text-xs text-ink-subtle">
            <span>{t('payment.transactionDate')}</span>
            <span>{formatDateWithTime(transaction?.timestamp)}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

function Row({ icon: Icon, label, value }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="flex items-center justify-between gap-3 text-ink-muted">
      <span className="inline-flex items-center gap-2">
        <Icon className="h-4 w-4 text-brand-500" />
        {label}
      </span>
      <span className="text-right font-medium text-ink rtl:text-left">{value}</span>
    </div>
  );
}

export default TransactionDetail;
